import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/FormField';
import { collection, addDoc, getDocs, query, where, orderBy, limit, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toast } from 'react-hot-toast';
import { validateProductEligibility, checkWortenEligibility, checkFnacEligibility } from '@/services/marketplaceEligibilityService';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, Transition } from '@headlessui/react';
import { Search, X, Loader2, ExternalLink, AlertTriangle } from 'lucide-react';
import { checkAmazonEligibility } from '@/services/amazonPriceService';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import { Link } from 'react-router-dom';

interface Product {
  id: string;
  name: string;
  sku: string;
  ean: string;
  regularPrice: number;
  stockQuantity: number;
  stockStatus: 'instock' | 'outofstock' | 'onbackorder';
  mainImage?: string;
}

interface ProductSubmission {
  productId: string;
  productName: string;
  sku: string;
  ean: string;
  proposedPrice: number;
  minimumPrice?: number;
  desiredChannel: string;
  supplierId: string;
  supplierName: string;
  status: 'approved_primary' | 'approved_reserve' | 'rejected';
  createdAt: Date;
  lastPriceInternal?: number;
  externalPrices?: Record<string, number>;
  rejectionReason?: string;
  mainImage?: string;
}

interface EligibilityResults {
  amazon?: {
    eligible: boolean;
    buyboxPrice: number | null;
    competitivePrice: number | null;
    totalOffers: number | null;
    ptPrice: number | null;
    esPrice: number | null;
    priceRange: string | null;
    reason?: string;
    recommendedPrice: number | null;
    supplierType: string | null;
    valueToReceive: number | null;
    rankValue: number | null;
    rankCategory: 'excellent' | 'good' | 'average' | 'moderate' | null;
    commission: number | null;
    commissionWithVAT: number | null;
    rankMessage?: string;
    notExistsInAmazon?: boolean;
  };
  worten?: {
    eligible: boolean;
    reason?: string;
    currentPrice?: number;
    suggestedPrice?: number;
    competitorUrl?: string;
    ptPrice?: number;
    commission?: number;
    commissionWithVAT?: number;
    valueToReceive?: number;
    priceWithoutVAT?: number;
    commissionValue?: number;
    vatOnCommission?: number;
    totalCommission?: number;
    basePrice?: number;
    recommendedPrice?: number;
  };
  fnac?: {
    eligible: boolean;
    reason?: string;
    basePrice?: number;
    commissionValue?: number;
    vatOnCommission?: number;
    valueToReceive?: number;
    recommendedPrice?: number;
  };
  kuantokusta?: {
    eligible: boolean;
    reason?: string;
    currentPrice?: number;
    suggestedPrice?: number;
    commission?: number;
    commissionWithVAT?: number;
    valueToReceive?: number;
    basePrice?: number;
    recommendedPrice?: number;
  };
}

export default function ProductSubmission() {
  const { currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [proposedPrice, setProposedPrice] = useState('');
  const [minimumPrice, setMinimumPrice] = useState('');
  const [showMinimumPriceModal, setShowMinimumPriceModal] = useState(false);
  const [desiredChannel, setDesiredChannel] = useState('');
  const [wortenProductUrl, setWortenProductUrl] = useState('');
  const [kuantoKustaProductUrl, setKuantoKustaProductUrl] = useState('');
  const [fnacProductUrl, setFnacProductUrl] = useState('');
  const [submissions, setSubmissions] = useState<ProductSubmission[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previousSubmissions, setPreviousSubmissions] = useState<ProductSubmission[]>([]);
  const [eligibilityResults, setEligibilityResults] = useState<EligibilityResults>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
  const [showCommissionDetails, setShowCommissionDetails] = useState(false);
  const [cachedAmazonData, setCachedAmazonData] = useState<any>(null);
  const [activeChannels, setActiveChannels] = useState<{[key: string]: boolean}>({});
  const [hasActiveChannels, setHasActiveChannels] = useState<boolean>(false);

  useEffect(() => {
    if (currentUser?.uid) {
      loadPreviousSubmissions();
      loadActiveChannels();
    }
  }, [currentUser]);

  useEffect(() => {
    if (selectedProduct) {
      setProposedPrice(parseFloat(selectedProduct.regularPrice).toFixed(2));
    }
  }, [selectedProduct]);

  const loadActiveChannels = async () => {
    if (!currentUser?.companyId) return;

    try {
      // Carregar estado de ativação da empresa
      const companyConfigDoc = await getDoc(doc(db, 'multicanal', currentUser.companyId));
      const companyConfig = companyConfigDoc.exists() ? companyConfigDoc.data() : {};

      // Definir canais ativos
      const channels = {
        amazon: companyConfig['amazon']?.active || false,
        worten: companyConfig['worten']?.active || false,
        fnac: companyConfig['fnac']?.active || false,
        kuantokusta: companyConfig['kuantokusta']?.active || false
      };

      // Verificar se há pelo menos um canal ativo
      const hasActive = Object.values(channels).some(isActive => isActive);
      setHasActiveChannels(hasActive);
      setActiveChannels(channels);
    } catch (error) {
      console.error('Erro ao carregar canais ativos:', error);
    }
  };

  const loadPreviousSubmissions = async () => {
    try {
      const q = query(
        collection(db, 'marketplaceProductSubmissions'),
        where('supplierId', '==', currentUser?.uid)
      );
      const querySnapshot = await getDocs(q);
      const submissions: ProductSubmission[] = [];
      
      querySnapshot.forEach((doc) => {
        submissions.push({
          ...doc.data() as ProductSubmission,
          id: doc.id
        } as ProductSubmission);
      });
      
      setPreviousSubmissions(submissions);
    } catch (error) {
      console.error('Error loading previous submissions:', error);
      toast.error('Erro ao carregar submissões anteriores');
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Digite um termo de busca (SKU, Nome ou EAN)');
      return;
    }

    setIsSearching(true);
    setSearchResults([]);
    setSelectedProduct(null);

    try {
      const productsRef = collection(db, 'products');
      const q = query(
        productsRef,
        where('companyId', '==', currentUser?.companyId),
        where('stockStatus', '==', 'instock')
      );
      
      const querySnapshot = await getDocs(q);
      const allProducts: Product[] = [];
      
      querySnapshot.forEach((doc) => {
        const productData = doc.data() as Product;
        allProducts.push({
          ...productData,
          id: doc.id
        });
      });
      
      const term = searchTerm.toLowerCase();
      const filteredProducts = allProducts.filter(product => 
        product.name?.toLowerCase().includes(term) || 
        product.sku?.toLowerCase().includes(term) || 
        product.ean?.toLowerCase().includes(term)
      );
      
      setSearchResults(filteredProducts);
      
      if (filteredProducts.length === 0) {
        toast.error('Nenhum produto encontrado com esse termo de busca');
      }
    } catch (error) {
      console.error('Error searching products:', error);
      toast.error('Erro ao buscar produtos');
    } finally {
      setIsSearching(false);
    }
  };

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    setEligibilityResults({});
    setProposedPrice('');
    setMinimumPrice('');
    setDesiredChannel('');
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEligibilityResults({});
    setProposedPrice('');
    setMinimumPrice('');
    setDesiredChannel('');
  };

  const handleCheckEligibility = async () => {
    if (!selectedProduct || !proposedPrice || !desiredChannel) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    // Validar URL da Worten se for o canal selecionado
    if (desiredChannel === 'worten') {
      if (!wortenProductUrl) {
        toast.error('Introduza o URL do produto na Worten');
        return;
      }
      if (!isValidWortenUrl(wortenProductUrl)) {
        toast.error('O URL deve ser de um produto da Worten (worten.pt)');
        return;
      }
    }

    // Validar URL do KuantoKusta se for o canal selecionado
    if (desiredChannel === 'kuantokusta') {
      if (!kuantoKustaProductUrl) {
        toast.error('Introduza o URL do produto no KuantoKusta');
        return;
      }
      if (!isValidKuantoKustaUrl(kuantoKustaProductUrl)) {
        toast.error('O URL deve ser de um produto do KuantoKusta (kuantokusta.pt)');
        return;
      }
    }

    // Validar URL da FNAC se for o canal selecionado
    if (desiredChannel === 'fnac') {
      if (!fnacProductUrl) {
        toast.error('Introduza o URL do produto na FNAC');
        return;
      }
      if (!isValidFnacUrl(fnacProductUrl)) {
        toast.error('O URL deve ser de um produto da FNAC (fnac.pt)');
        return;
      }
    }

    setIsCheckingEligibility(true);
    setEligibilityResults({});
    
    try {
      const eligibilityPromises = [];
      const results: any = {};
      
      if (desiredChannel === 'amazon') {
        const result = await checkAmazonEligibility(selectedProduct.ean, parseFloat(proposedPrice));
        
        // Verificar se o produto existe na Amazon
        if (result.reason === "Produto não encontrado na Amazon") {
          // Produto não existe na Amazon, mas pode ser submetido
          results.amazon = {
            ...result,
            eligible: true, // Tornar elegível mesmo sem existir na Amazon
            isEligible: true,
            reason: "Este produto poderá demorar um pouco mais de tempo visto que deverá ser criado em primeiro lugar, visto que não existe na Amazon",
            notExistsInAmazon: true, // Flag para indicar que não existe na Amazon
            ptPrice: null,
            esPrice: null,
            buyboxPrice: null,
            totalOffers: 0,
            recommendedPrice: parseFloat(proposedPrice), // Usar o preço proposto como recomendado
            commission: result.commission || (parseFloat(proposedPrice) * 0.20), // Estimar comissão padrão de 20%
            commissionWithVAT: result.commissionWithVAT || ((parseFloat(proposedPrice) * 0.23) * 1.23), // Estimar comissão com IVA
            valueToReceive: result.valueToReceive || (parseFloat(proposedPrice) * 0.77) // Estimar valor a receber
          };
        } else {
          // Processar o resultado para garantir que a elegibilidade seja correta
          results.amazon = {
            ...result,
            eligible: result.recommendedPrice ? parseFloat(proposedPrice) <= result.recommendedPrice : false,
            isEligible: result.recommendedPrice ? parseFloat(proposedPrice) <= result.recommendedPrice : false,
            ptPrice: result.ptPrice,
            esPrice: result.esPrice,
            buyboxPrice: result.buyboxPrice,
            totalOffers: result.totalOffers,
            recommendedPrice: result.recommendedPrice,
            commission: result.commission,
            commissionWithVAT: result.commissionWithVAT,
            valueToReceive: result.valueToReceive,
            rankMessage: result.rankMessage
          };
        }
      }
      
      if (desiredChannel === 'worten') {
        eligibilityPromises.push(
          checkWortenEligibility(
            parseFloat(proposedPrice),
            wortenProductUrl
          ).then(result => {
            results.worten = result;
          })
        );
      }
      
      if (desiredChannel === 'fnac') {
        eligibilityPromises.push(
          validateProductEligibility(
            selectedProduct,
            parseFloat(proposedPrice),
            desiredChannel,
            fnacProductUrl
          ).then(result => {
            results.fnac = result;
          })
        );
      }
      
      if (desiredChannel === 'kuantokusta') {
        eligibilityPromises.push(
          validateProductEligibility(
            selectedProduct,
            parseFloat(proposedPrice),
            desiredChannel,
            kuantoKustaProductUrl
          ).then(result => {
            results.kuantokusta = result;
          })
        );
      }
      
      await Promise.all(eligibilityPromises);
      setEligibilityResults(results);
    } catch (error) {
      console.error('Erro ao verificar elegibilidade:', error);
      toast.error('Erro ao verificar elegibilidade. Tente novamente.');
    } finally {
      setIsCheckingEligibility(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProduct) {
      toast.error('Selecione um produto');
      return;
    }

    if (!proposedPrice) {
      toast.error('Defina um preço proposto');
      return;
    }

    if (!desiredChannel) {
      toast.error('Selecione um canal');
      return;
    }

    // Fechar o modal atual antes de abrir o modal de preço mínimo
    setIsModalOpen(false);
    // Mostrar modal para definir preço mínimo
    setShowMinimumPriceModal(true);
  };

  const handleFinalSubmit = async () => {
    try {
      setIsSubmitting(true);
      
      const formattedPrice = parseFloat(parseFloat(proposedPrice).toFixed(2));
      const formattedMinPrice = minimumPrice ? parseFloat(parseFloat(minimumPrice).toFixed(2)) : null;

      // Validar preço mínimo
      if (!formattedMinPrice) {
        if (!window.confirm('ATENÇÃO: Não definir um preço mínimo pode resultar na anulação da sua oferta caso perca a BuyBox. Tem certeza que deseja continuar sem definir um preço mínimo?')) {
          setIsSubmitting(false);
          return;
        }
      } else if (formattedMinPrice > formattedPrice) {
        toast.error('O preço mínimo não pode ser maior que o preço proposto');
        setIsSubmitting(false);
        return;
      }

      // 1. Verificar se o usuário atual já tem uma submissão ativa
      const userSubmissionQuery = query(
        collection(db, 'marketplaceProductSubmissions'),
        where('productId', '==', selectedProduct.id),
        where('desiredChannel', '==', desiredChannel),
        where('submittedBy', '==', currentUser?.uid),
        where('status', 'in', ['approved_primary', 'approved_reserve'])
      );

      const userSubmissions = await getDocs(userSubmissionQuery);
      
      if (!userSubmissions.empty) {
        toast.error('Você já tem uma submissão ativa para este produto neste canal');
        setIsSubmitting(false);
        return;
      }

      // 2. Buscar submissões existentes de outros usuários
      const existingSubmissionsQuery = query(
        collection(db, 'marketplaceProductSubmissions'),
        where('productId', '==', selectedProduct.id),
        where('desiredChannel', '==', desiredChannel),
        where('status', 'in', ['approved_primary', 'approved_reserve'])
      );

      const existingSubmissions = await getDocs(existingSubmissionsQuery);
      const submissions = existingSubmissions.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Ordenar por preço (mais barato primeiro)
      submissions.sort((a, b) => a.proposedPrice - b.proposedPrice);

      // 3. Determinar o status baseado nas submissões existentes
      let newStatus = 'approved_primary'; // Por padrão, aprova como principal
      
      if (submissions.length === 0) {
        // Nenhuma submissão existente - será aprovado como principal
        newStatus = 'approved_primary';
      } else if (submissions.length === 1) {
        // Já existe um fornecedor
        if (formattedPrice < submissions[0].proposedPrice) {
          // Novo preço é mais baixo - será principal, existente vira reserva
          newStatus = 'approved_primary';
          await updateDoc(doc(db, 'marketplaceProductSubmissions', submissions[0].id), {
            status: 'approved_reserve'
          });
        } else {
          // Novo preço é mais alto - será reserva
          newStatus = 'approved_reserve';
        }
      } else if (submissions.length === 2) {
        // Já existem dois fornecedores
        if (formattedPrice < submissions[0].proposedPrice) {
          // Novo preço é o mais baixo
          newStatus = 'approved_primary';
          // Principal atual vira reserva
          await updateDoc(doc(db, 'marketplaceProductSubmissions', submissions[0].id), {
            status: 'approved_reserve'
          });
          // Reserva atual é removido
          await updateDoc(doc(db, 'marketplaceProductSubmissions', submissions[1].id), {
            status: 'rejected',
            rejectionReason: 'Substituído por novo fornecedor com preço mais competitivo'
          });
        } else if (formattedPrice < submissions[1].proposedPrice) {
          // Novo preço é menor que o reserva
          newStatus = 'approved_reserve';
          // Reserva atual é removido
          await updateDoc(doc(db, 'marketplaceProductSubmissions', submissions[1].id), {
            status: 'rejected',
            rejectionReason: 'Substituído por novo fornecedor com preço mais competitivo'
          });
        } else {
          // Novo preço é o mais alto - rejeitar
          toast.error('Preço não competitivo. Já existem dois fornecedores com preços mais baixos.');
          setIsSubmitting(false);
          return;
        }
      }

      // 4. Criar nova submissão (já aprovada)
      const submissionData = {
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        sku: selectedProduct.sku,
        ean: selectedProduct.ean,
        proposedPrice: formattedPrice,
        minimumPrice: formattedMinPrice,
        desiredChannel,
        status: newStatus, // Será 'approved_primary' ou 'approved_reserve'
        submittedAt: new Date(),
        approvedAt: new Date(),
        submittedBy: currentUser?.uid,
        submitterName: currentUser?.displayName || 'Usuário',
        mainImage: selectedProduct.mainImage || '',
        stockQuantity: selectedProduct.stockQuantity || 0,
        // Garantir que campos opcionais tenham valores padrão
        lastPriceInternal: eligibilityResults[desiredChannel]?.lastPriceInternal || null,
        externalPrices: eligibilityResults[desiredChannel]?.externalPrices || null,
        approvalLogic: eligibilityResults[desiredChannel]?.approvalLogic || null,
        suggestedPrice: eligibilityResults[desiredChannel]?.suggestedPrice || null,
        eligibilityType: eligibilityResults[desiredChannel]?.eligibilityType || null,
        // Adicionar campos do resultado da elegibilidade
        buyboxPrice: eligibilityResults[desiredChannel]?.buyboxPrice || null,
        ptPrice: eligibilityResults[desiredChannel]?.ptPrice || null,
        esPrice: eligibilityResults[desiredChannel]?.esPrice || null,
        totalOffers: eligibilityResults[desiredChannel]?.totalOffers || 0,
        recommendedPrice: eligibilityResults[desiredChannel]?.recommendedPrice || null,
        commission: eligibilityResults[desiredChannel]?.commission || null,
        commissionVAT: eligibilityResults[desiredChannel]?.commissionWithVAT ? 
          eligibilityResults[desiredChannel]?.commissionWithVAT - eligibilityResults[desiredChannel]?.commission : null,
        commissionWithVAT: eligibilityResults[desiredChannel]?.commissionWithVAT,
        valueToReceive: eligibilityResults[desiredChannel]?.valueToReceive
      };

      // Remover campos undefined ou null
      Object.keys(submissionData).forEach(key => {
        if (submissionData[key] === undefined || submissionData[key] === null) {
          delete submissionData[key];
        }
      });

      await addDoc(collection(db, 'marketplaceProductSubmissions'), submissionData);

      // 5. Mostrar mensagem de sucesso específica
      const successMessage = newStatus === 'approved_primary' 
        ? 'Produto aprovado como fornecedor principal!'
        : 'Produto aprovado como fornecedor reserva!';
      
      toast.success(successMessage);
      setSelectedProduct(null);
      setProposedPrice('');
      setMinimumPrice('');
      setDesiredChannel('');
      setEligibilityResults({});
      setIsModalOpen(false);
      setShowMinimumPriceModal(false);
      loadPreviousSubmissions();
    } catch (error) {
      console.error('Error submitting product:', error);
      toast.error('Erro ao submeter produto');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const regex = /^\d*\.?\d{0,2}$/;
    
    if (regex.test(value) || value === '') {
      setProposedPrice(value);
      // Clear eligibility results when price changes
      setEligibilityResults({});
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'approved_primary': return 'Aprovado (Principal)';
      case 'approved_reserve': return 'Aprovado (Reserva)';
      case 'rejected': return 'Rejeitado';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved_primary': return 'text-emerald-600';
      case 'approved_reserve': return 'text-blue-600';
      case 'rejected': return 'text-red-600';
      default: return '';
    }
  };

  const renderEligibilityResults = () => {
    if (!eligibilityResults) return null;

    return (
      <div className="mt-4">
        {eligibilityResults.fnac && (
          <div className="mb-4 p-3 border border-l-4 border-l-blue-600 bg-white rounded-sm shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">FNAC</span>
              <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                eligibilityResults.fnac.eligible
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {eligibilityResults.fnac.eligible ? 'Elegível' : 'Não Elegível'}
              </span>
            </div>

            {/* Razão da Elegibilidade */}
            {eligibilityResults.fnac.reason && (
              <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-700">
                {eligibilityResults.fnac.reason}
              </div>
            )}

            {/* Preços base */}
            <div className="p-2 bg-primary/5 rounded space-y-1 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Preço base:</span>
                <span className="text-sm">
                  {formatCurrency(eligibilityResults.fnac.basePrice)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Preço mínimo recomendado:</span>
                <span className="text-sm font-medium">
                  {formatCurrency(eligibilityResults.fnac.recommendedPrice)}
                </span>
              </div>
            </div>

            {/* Comissões */}
            <div className="p-2 bg-gray-50 rounded space-y-1 mt-3">
              <button 
                onClick={() => setShowCommissionDetails(!showCommissionDetails)}
                className="w-full flex justify-between items-center text-sm text-gray-600 hover:text-gray-800"
              >
                <span>Detalhes da comissão</span>
                <span className="text-xs">{showCommissionDetails ? '▼' : '▶'}</span>
              </button>

              {showCommissionDetails && (
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Comissão base (20%):</span>
                    <span className="text-sm">
                      {formatCurrency(eligibilityResults.fnac.commissionValue)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">IVA sobre comissão (23%):</span>
                    <span className="text-sm">
                      {formatCurrency(eligibilityResults.fnac.vatOnCommission)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Valor a receber */}
            <div className="p-2 bg-green-50 rounded mt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  Valor que receberá se vender a {formatCurrency(eligibilityResults.fnac.recommendedPrice)}:
                </span>
                <span className="font-medium text-green-700">
                  {formatCurrency(eligibilityResults.fnac.valueToReceive)}
                </span>
              </div>
            </div>

            {/* Link para o Produto */}
            {eligibilityResults.fnac.competitorUrl && (
              <div className="mt-2">
                <a 
                  href={eligibilityResults.fnac.competitorUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                >
                  <ExternalLink size={14} />
                  Ver na FNAC
                </a>
              </div>
            )}
          </div>
        )}

        {eligibilityResults.worten && (
          <div className="mb-4 p-3 border border-l-4 border-l-blue-600 bg-white rounded-sm shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Worten</span>
              <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                eligibilityResults.worten.eligible
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {eligibilityResults.worten.eligible ? 'Elegível' : 'Não Elegível'}
              </span>
            </div>

            {/* Razão da Elegibilidade */}
            {eligibilityResults.worten.reason && (
              <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-700">
                {eligibilityResults.worten.reason}
              </div>
            )}

            {/* Preços base */}
            <div className="p-2 bg-primary/5 rounded space-y-1 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Preço base:</span>
                <span className="text-sm">
                  {formatCurrency(eligibilityResults.worten.basePrice)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Preço mínimo recomendado:</span>
                <span className="text-sm font-medium">
                  {formatCurrency(eligibilityResults.worten.recommendedPrice)}
                </span>
              </div>
            </div>

            {/* Comissões */}
            <div className="p-2 bg-gray-50 rounded space-y-1 mt-3">
              <button 
                onClick={() => setShowCommissionDetails(!showCommissionDetails)}
                className="w-full flex justify-between items-center text-sm text-gray-600 hover:text-gray-800"
              >
                <span>Detalhes da comissão</span>
                <span className="text-xs">{showCommissionDetails ? '▼' : '▶'}</span>
              </button>

              {showCommissionDetails && (
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Comissão base (20%):</span>
                    <span className="text-sm">
                      {formatCurrency(eligibilityResults.worten.commissionValue)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">IVA sobre comissão (23%):</span>
                    <span className="text-sm">
                      {formatCurrency(eligibilityResults.worten.vatOnCommission)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Valor a receber */}
            <div className="p-2 bg-green-50 rounded mt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  Valor que receberá se vender a {formatCurrency(eligibilityResults.worten.recommendedPrice)}:
                </span>
                <span className="font-medium text-green-700">
                  {formatCurrency(eligibilityResults.worten.valueToReceive)}
                </span>
              </div>
            </div>

            {/* Link para o Produto */}
            {eligibilityResults.worten.competitorUrl && (
              <div className="mt-2">
                <a 
                  href={eligibilityResults.worten.competitorUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                >
                  <ExternalLink size={14} />
                  Ver na Worten
                </a>
              </div>
            )}
          </div>
        )}

        {eligibilityResults.amazon && (
          <div className="mb-4 p-3 border border-l-4 border-l-secondary bg-white rounded-sm shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Amazon</span>
              <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                eligibilityResults.amazon.eligible
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {eligibilityResults.amazon.eligible ? 'Elegível' : 'Não Elegível'}
              </span>
            </div>

            {/* Razão da Elegibilidade */}
            {eligibilityResults.amazon.reason && (
              <div className={`mt-2 p-2 rounded text-sm ${eligibilityResults.amazon.notExistsInAmazon ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' : 'bg-green-50 text-green-700'}`}>
                {eligibilityResults.amazon.reason}
              </div>
            )}

            {/* Preços base */}
            <div className="p-2 bg-primary/5 rounded space-y-1 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Preço base:</span>
                <span className="text-sm">
                  {formatCurrency(eligibilityResults.amazon.buyboxPrice || eligibilityResults.amazon.ptPrice)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Preço mínimo recomendado:</span>
                <span className="text-sm font-medium">
                  {formatCurrency(eligibilityResults.amazon.recommendedPrice)}
                </span>
              </div>
            </div>

            {/* Comissões */}
            <div className="p-2 bg-gray-50 rounded space-y-1 mt-3">
              <button 
                onClick={() => setShowCommissionDetails(!showCommissionDetails)}
                className="w-full flex justify-between items-center text-sm text-gray-600 hover:text-gray-800"
              >
                <span>Detalhes da comissão</span>
                <span className="text-xs">{showCommissionDetails ? '▼' : '▶'}</span>
              </button>

              {showCommissionDetails && (
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Comissão base (20%):</span>
                    <span className="text-sm">
                      {formatCurrency(eligibilityResults.amazon.commission)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">IVA sobre comissão (23%):</span>
                    <span className="text-sm">
                      {formatCurrency(eligibilityResults.amazon.commissionWithVAT - eligibilityResults.amazon.commission)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Valor a receber */}
            <div className="p-2 bg-green-50 rounded mt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  Valor que receberá se vender a {formatCurrency(eligibilityResults.amazon.recommendedPrice)}:
                </span>
                <span className="font-medium text-green-700">
                  {formatCurrency(eligibilityResults.amazon.valueToReceive)}
                </span>
              </div>
            </div>

            {/* Link para o Produto */}
            {eligibilityResults.amazon.competitorUrl && (
              <div className="mt-2">
                <a 
                  href={eligibilityResults.amazon.competitorUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                >
                  <ExternalLink size={14} />
                  Ver na Amazon
                </a>
              </div>
            )}
          </div>
        )}

        {eligibilityResults.kuantokusta && (
          <div className="mb-4 p-3 border border-l-4 border-l-blue-600 bg-white rounded-sm shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">KuantoKusta</span>
              <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                eligibilityResults.kuantokusta.eligible
                  ? 'bg-green-100 text-green-800'
                  : 'bg-red-100 text-red-800'
              }`}>
                {eligibilityResults.kuantokusta.eligible ? 'Elegível' : 'Não Elegível'}
              </span>
            </div>

            {/* Razão da Elegibilidade */}
            {eligibilityResults.kuantokusta.reason && (
              <div className="mt-2 p-2 bg-green-50 rounded text-sm text-green-700">
                {eligibilityResults.kuantokusta.reason}
              </div>
            )}

            {/* Preços base */}
            <div className="p-2 bg-primary/5 rounded space-y-1 mt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Preço base:</span>
                <span className="text-sm">
                  {formatCurrency(eligibilityResults.kuantokusta.basePrice)}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-600">Preço mínimo recomendado:</span>
                <span className="text-sm font-medium">
                  {formatCurrency(eligibilityResults.kuantokusta.recommendedPrice)}
                </span>
              </div>
            </div>

            {/* Comissões */}
            <div className="p-2 bg-gray-50 rounded space-y-1 mt-3">
              <button 
                onClick={() => setShowCommissionDetails(!showCommissionDetails)}
                className="w-full flex justify-between items-center text-sm text-gray-600 hover:text-gray-800"
              >
                <span>Detalhes da comissão</span>
                <span className="text-xs">{showCommissionDetails ? '▼' : '▶'}</span>
              </button>

              {showCommissionDetails && (
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Comissão base (20%):</span>
                    <span className="text-sm">
                      {formatCurrency(eligibilityResults.kuantokusta.commissionValue)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">IVA sobre comissão (23%):</span>
                    <span className="text-sm">
                      {formatCurrency(eligibilityResults.kuantokusta.vatOnCommission)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Valor a receber */}
            <div className="p-2 bg-green-50 rounded mt-3">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">
                  Valor que receberá se vender a {formatCurrency(eligibilityResults.kuantokusta.recommendedPrice)}:
                </span>
                <span className="font-medium text-green-700">
                  {formatCurrency(eligibilityResults.kuantokusta.valueToReceive)}
                </span>
              </div>
            </div>

            {/* Link para o Produto */}
            {eligibilityResults.kuantokusta.competitorUrl && (
              <div className="mt-2">
                <a 
                  href={eligibilityResults.kuantokusta.competitorUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm"
                >
                  <ExternalLink size={14} />
                  Ver no KuantoKusta
                </a>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // Função auxiliar para formatar moeda com validação de tipo
  const formatCurrency = (value: any): string => {
    if (value === null || value === undefined || isNaN(Number(value))) {
      return '-';
    }
    const numValue = Number(value);
    return `${numValue.toFixed(2).replace('.', ',')}€`;
  };

  const isValidWortenUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes('worten.pt');
    } catch {
      return false;
    }
  };

  const isValidKuantoKustaUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes('kuantokusta.pt');
    } catch {
      return false;
    }
  };

  const isValidFnacUrl = (url: string) => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes('fnac.pt');
    } catch {
      return false;
    }
  };

  const validateProductUrl = (url: string, channel: string) => {
    switch (channel.toLowerCase()) {
      case 'worten':
        return isValidWortenUrl(url);
      case 'kuantokusta':
        return isValidKuantoKustaUrl(url);
      case 'fnac':
        return isValidFnacUrl(url);
      default:
        return true;
    }
  };

  const getUrlPlaceholder = (channel: string) => {
    switch (channel.toLowerCase()) {
      case 'worten':
        return 'https://www.worten.pt/produto/...';
      case 'kuantokusta':
        return 'https://www.kuantokusta.pt/produto/...';
      case 'fnac':
        return 'https://www.fnac.pt/produto/...';
      default:
        return '';
    }
  };

  const channelRequiresUrl = (channel: string) => {
    return ['worten', 'kuantokusta', 'fnac'].includes(channel.toLowerCase());
  };

  const renderChannelOptions = () => {
    return (
      <>
        <option value="">Selecione um canal</option>
        {activeChannels.amazon && <option value="amazon">Amazon</option>}
        {activeChannels.worten && <option value="worten">Worten</option>}
        {activeChannels.fnac && <option value="fnac">Fnac</option>}
        {activeChannels.kuantokusta && <option value="kuantokusta">KuantoKusta</option>}
      </>
    );
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Submeter Produtos para Marketplace</h1>
      
      {!hasActiveChannels && (
        <div className="mb-6 p-4 border border-amber-300 bg-amber-50 rounded-md">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="h-6 w-6 text-amber-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-amber-800">Nenhum canal de marketplace ativo</h3>
              <p className="text-amber-700 mt-1">Para submeter produtos, é necessário ativar pelo menos um canal de marketplace.</p>
              <Link to="/dashboard/multicanal/activations">
                <Button className="mt-3 bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2">
                  Ativar Canais de Marketplace
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 gap-6 mt-6">
        <Card>
          <CardHeader>
            <h2 className="text-3xl font-bold tracking-tight">Submissão de Produtos</h2>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex space-x-2">
                <FormField
                  label="Buscar por SKU, Nome ou EAN"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Digite para buscar"
                  className="flex-1"
                />
                <Button 
                  onClick={handleSearch} 
                  className="mt-6"
                  disabled={isSearching}
                >
                  {isSearching ? 'Buscando...' : 'Buscar'}
                </Button>
              </div>
              
              {searchResults.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-lg font-medium mb-2">Resultados da Busca ({searchResults.length})</h3>
                  <div className="border rounded-md overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Imagem</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">EAN</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preço</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {searchResults.map((product) => (
                          <tr key={product.id} className={selectedProduct?.id === product.id ? 'bg-blue-50' : ''}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {product.mainImage ? (
                                <img src={product.mainImage} alt={product.name} className="h-10 w-10 object-cover rounded-md" />
                              ) : (
                                <div className="h-10 w-10 bg-gray-200 rounded-md flex items-center justify-center text-gray-500">N/A</div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">{product.name}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{product.sku}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{product.ean}</td>
                            <td className="px-6 py-4 whitespace-nowrap">{parseFloat(product.regularPrice).toFixed(2)} €</td>
                            <td className="px-6 py-4 whitespace-nowrap">{product.stockQuantity}</td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Button 
                                onClick={() => handleProductSelect(product)} 
                                size="sm"
                                className={selectedProduct?.id === product.id ? 'bg-green-600 hover:bg-green-700' : ''}
                              >
                                {selectedProduct?.id === product.id ? 'Selecionado' : 'Selecionar'}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <h2 className="text-3xl font-bold tracking-tight">Minhas Submissões</h2>
          </CardHeader>
          <CardContent>
            {previousSubmissions.length === 0 ? (
              <p className="text-gray-500">Nenhuma submissão encontrada.</p>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produto</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU/EAN</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preço</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Canal</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Data</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {previousSubmissions.map((submission) => (
                      <tr key={submission.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {submission.mainImage ? (
                              <img src={submission.mainImage} alt={submission.productName} className="h-10 w-10 object-cover rounded-md" />
                            ) : (
                              <div className="h-10 w-10 bg-gray-200 rounded-md flex items-center justify-center text-gray-500">N/A</div>
                            )}
                            <span>{submission.productName}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <p className="text-sm">SKU: {submission.sku}</p>
                            <p className="text-sm text-gray-600">EAN: {submission.ean}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{parseFloat(submission.proposedPrice).toFixed(2)} €</td>
                        <td className="px-6 py-4 whitespace-nowrap capitalize">{submission.desiredChannel}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(submission.status)}`}>
                            {getStatusLabel(submission.status)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {submission.submittedAt && typeof submission.submittedAt.toDate === 'function' 
                            ? submission.submittedAt.toDate().toLocaleDateString() 
                            : new Date().toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Modal de Submissão de Produto */}
      <Transition appear show={isModalOpen} as={React.Fragment}>
        <Dialog as="div" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClose={handleCloseModal}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Submeter Produto para Marketplace</h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 p-4">
              {selectedProduct && (
                <div className="p-3 border rounded-md bg-gray-50 mb-2 flex items-start space-x-3">
                  <div className="flex-shrink-0">
                    {selectedProduct.mainImage ? (
                      <img src={selectedProduct.mainImage} alt={selectedProduct.name} className="h-14 w-14 object-cover rounded-md" />
                    ) : (
                      <div className="h-14 w-14 bg-gray-200 rounded-md flex items-center justify-center text-gray-500">N/A</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{selectedProduct.name}</p>
                    <p className="text-xs text-gray-500">SKU: {selectedProduct.sku}</p>
                    <p className="text-xs text-gray-500">EAN: {selectedProduct.ean}</p>
                    <div className="flex items-center mt-1">
                      <span className="text-xs font-medium">Preço Regular:</span>
                      <span className="text-xs ml-1">{selectedProduct.regularPrice.toFixed(2)} €</span>
                      <span className="text-xs font-medium ml-2">Stock:</span>
                      <span className="text-xs ml-1">{selectedProduct.stockQuantity} unidades</span>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <FormField
                    label="Preço Proposto (€)"
                    type="number"
                    step="0.01"
                    value={proposedPrice}
                    onChange={(e) => setProposedPrice(e.target.value)}
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <FormField
                    label="Canal"
                    type="select"
                    value={desiredChannel}
                    onChange={(e) => {
                      setDesiredChannel(e.target.value);
                      setEligibilityResults({});
                      // Limpar URLs quando mudar de canal
                      if (e.target.value !== 'worten') {
                        setWortenProductUrl('');
                      }
                      if (e.target.value !== 'kuantokusta') {
                        setKuantoKustaProductUrl('');
                      }
                      if (e.target.value !== 'fnac') {
                        setFnacProductUrl('');
                      }
                    }}
                    required
                  >
                    {renderChannelOptions()}
                  </FormField>
                </div>
              </div>
              
              {desiredChannel === 'worten' && (
                <FormField
                  label="URL do Produto na Worten"
                  type="url"
                  value={wortenProductUrl}
                  onChange={(e) => setWortenProductUrl(e.target.value)}
                  placeholder={getUrlPlaceholder(desiredChannel)}
                  required
                />
              )}

              {desiredChannel === 'kuantokusta' && (
                <FormField
                  label="URL do Produto no KuantoKusta"
                  type="url"
                  value={kuantoKustaProductUrl}
                  onChange={(e) => setKuantoKustaProductUrl(e.target.value)}
                  placeholder={getUrlPlaceholder(desiredChannel)}
                  required
                />
              )}

              {desiredChannel === 'fnac' && (
                <FormField
                  label="URL do Produto na Fnac"
                  type="url"
                  value={fnacProductUrl}
                  onChange={(e) => setFnacProductUrl(e.target.value)}
                  placeholder={getUrlPlaceholder(desiredChannel)}
                  required
                />
              )}
              
              {selectedProduct && proposedPrice && desiredChannel && (
                <Button 
                  type="button" 
                  onClick={handleCheckEligibility} 
                  className="w-full bg-primary hover:bg-primary-dark text-gray-800"
                  disabled={isCheckingEligibility}
                >
                  {isCheckingEligibility ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    'Verificar Elegibilidade'
                  )}
                </Button>
              )}
              
              {renderEligibilityResults()}
              
              <div className="mt-4 flex justify-end space-x-2">
                <Button onClick={handleCloseModal} variant="outline">
                  Cancelar
                </Button>
                {((desiredChannel === 'amazon' && (eligibilityResults.amazon?.eligible || eligibilityResults.amazon?.reason === "Produto não encontrado na Amazon" || eligibilityResults.amazon?.notExistsInAmazon)) ||
                  (desiredChannel === 'worten' && eligibilityResults.worten?.eligible) ||
                  (desiredChannel === 'fnac' && eligibilityResults.fnac?.eligible) ||
                  (desiredChannel === 'kuantokusta' && eligibilityResults.kuantokusta?.eligible)) && (
                  <Button 
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="bg-primary text-white hover:bg-primary/90"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Submeter Oferta'
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Modal de Preço Mínimo */}
      <Dialog
        open={showMinimumPriceModal}
        onClose={() => setShowMinimumPriceModal(false)}
        className="fixed inset-0 z-[100] overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen">
          <Dialog.Overlay className="fixed inset-0 bg-black opacity-30 z-[100]" />

          <div className="relative bg-white rounded-lg p-6 max-w-md w-full mx-4 z-[101]">
            <div className="mb-6">
              <h3 className="text-lg font-semibold">Definir Preço Mínimo</h3>
              
              <div className="mb-4 p-3 bg-primary/10 rounded-md border border-primary/20">
                <div className="text-sm text-gray-600">Seu preço proposto:</div>
                <div className="text-lg font-semibold">{parseFloat(proposedPrice).toFixed(2)}€</div>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Para maximizar suas chances de venda, o sistema irá:
              </p>
              <ul className="text-sm text-gray-600 mb-4 list-disc pl-5 space-y-2">
                <li>Aumentar automaticamente seu preço (sem limite) quando possível para maximizar seu lucro</li>
                <li>Reduzir gradualmente o preço em incrementos de 0,10€ para manter a BuyBox quando necessário</li>
                <li>Nunca reduzir abaixo do preço mínimo que você definir</li>
              </ul>
              <p className="text-sm text-gray-600 mb-4">
                <strong>Recomendação:</strong> Para manter-se competitivo, considere definir um preço mínimo entre 10% a 20% abaixo do seu preço proposto ({(parseFloat(proposedPrice) * 0.8).toFixed(2)}€ - {(parseFloat(proposedPrice) * 0.9).toFixed(2)}€)
              </p>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preço mínimo aceitável
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  max={parseFloat(proposedPrice)}
                  value={minimumPrice}
                  onChange={(e) => setMinimumPrice(e.target.value)}
                  className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder={`Valor entre 0 e ${parseFloat(proposedPrice).toFixed(2)}€`}
                />
                {parseFloat(minimumPrice) > parseFloat(proposedPrice) && (
                  <p className="mt-1 text-sm text-red-600">
                    O preço mínimo não pode ser maior que o preço proposto
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowMinimumPriceModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleFinalSubmit}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded hover:bg-primary/90 disabled:opacity-50"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Confirmar'
                )}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
